import { Checkbox, DefaultButton, IPersonaProps, MessageBar, MessageBarType, NormalPeoplePicker, Panel, PanelType, Pivot, PivotItem, PrimaryButton, Stack, TextField, Toggle } from "office-ui-fabric-react"
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { saveRoom } from "../../../api/rooms";
import { ProfileContext } from "../../../Home";
import { IBookingConstraint } from "../../../types/Booking.types";
import { IRoom } from "../../../types/SiteLocation.types"
import Utils from "../../../utils";
import { RestrictionsSection, RestrictionUpdate } from "./RestrictionsSection";
import QRCode from "qrcode";

export interface IRoomFormPanelProps {
    onDismiss: (reloadLocations?: boolean) => void;
    room: Partial<IRoom>;
    asRoomApprover?: boolean;
}

export const RoomFormPanel = (props: IRoomFormPanelProps) => {
    const { room, asRoomApprover } = props;

    const userContext = useContext(ProfileContext);

    const validForm = (title: string|undefined) => {
        return !(!title || title.trim() === "");
    };

    let initialSiteAdminUsers: IPersonaProps[] = [];
    if (room.ResourceAdminUsers) {
        initialSiteAdminUsers = room.ResourceAdminUsers.filter((sau) => sau.AccessType === 1).map((user) => ({
            text: user.FullName,
            secondaryText: user.Email,
            data: {
                userId: user.Id
            }
        } as IPersonaProps));
    }

    // Form variables
    const [ name, setName ] = useState<string|undefined>(room.Name);
    const [ capacity, setCapacity ] = useState<number|undefined>(room.Capacity);
    const [ equipment, setEquipment ] = useState<string|undefined>(room.Equipment);
    const [ disabled, setDisabled ] = useState<boolean|undefined>(room.Disabled);
    const [ hidden, setHidden ] = useState<boolean|undefined>(room.Hidden);

    // Include teams in user searches
    const [roomIncludeTeams, setRoomIncludeTeams] = useState<boolean|undefined>(false);

    // Booking constraints
    const [roomConstraint, setRoomConstraint] = useState<IBookingConstraint | undefined>(room.RoomBookingConstraint);
    const [enableCheckIn, setEnableCheckIn] = useState<boolean | undefined>(room.RoomCheckIns);

    // Booking approval / access
    const [ restrictRoomAccess, setRestrictRoomAccess ] = useState<boolean|undefined>(room.RestrictRoomAccess);
    const [ roomAdmins, setRoomAdmins ] = useState<IPersonaProps[]|undefined>(initialSiteAdminUsers);

    // Validation
    const [ error, setError ] = useState<string|undefined>();
    const [ formValid, setFormValid ] = useState(validForm(name));

    // QR Code
    const [ qrCode, setQrCode ] = useState<string|undefined>();

    const getTitleCasedValues = (obj: {[index: string]: any}) => {
        const returnObj: {[index: string]: any} = {};
        for (let key in obj) {
            const titleCasedKey = key.charAt(0).toUpperCase() + key.substr(1);
            returnObj[titleCasedKey] = obj[key]
        }
        return returnObj;
    };

    const _saveForm = async () => {
        const formValues = getTitleCasedValues({ name, capacity, equipment, hidden, disabled, restrictRoomAccess });
        const roomAdminUserIds = roomAdmins?.filter((sa) => Boolean((sa as any).data?.userId))?.map((sa: any) => sa.data.userId);
        const roomAdminTeamIds = roomAdmins?.filter((sa) => Boolean((sa as any).data?.teamId))?.map((sa: any) => sa.data.teamId);
        const response = await saveRoom({
            ...room, 
            ...formValues,
            RoomAdminUsers: roomAdminUserIds || [],
            RoomAdminTeams: roomAdminTeamIds || [],
            BookingConstraints: [roomConstraint].filter((bc) => Boolean(bc)),
            RoomCheckIns: enableCheckIn
        } as IRoom);
        if (response.ok) {
            if (userContext) {
                if (
                    roomAdminUserIds && roomAdminUserIds.find((sa) => sa === userContext.Profile.Id)
                ) {
                    const abortController = new AbortController();
                    userContext.onProfileUpdated(abortController);
                }
            }
            props.onDismiss(true);
        } else {
            setError("Unable to save room: " + response.statusText);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const mapUserToPersona = useCallback(Utils.mapToPersona, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const roomUserSearch = useCallback(Utils.userSearch(roomIncludeTeams), [mapUserToPersona, roomIncludeTeams]);

    useEffect(() => {
        setFormValid(validForm(name));
    }, [name]);

    useEffect(() => {
        const roomName = room.Name?.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '-');
        QRCode.toDataURL(`${window.location.protocol}//${window.location.host}/check-in/${room.SiteId}/${room.LocationId}/${room.Id}?rn=${roomName}`).then((code) => {
            setQrCode(code);
        });
    }, [room.Id, room.Name, room.LocationId, room.SiteId]);

    return (
        <Panel 
            type={PanelType.medium} 
            isOpen={true} 
            onDismiss={() => props.onDismiss()}
            headerText={room.Id === undefined ? 'New Room' : `Edit Room: ${props.room.Name || ""}`}
            onRenderFooter={() => <Stack horizontal horizontalAlign="end" style={{padding: 20}}>
                <PrimaryButton disabled={!formValid} iconProps={{iconName: "Save"}} text="Save" onClick={() => { _saveForm() }} style={{marginRight: 20}} />
                <DefaultButton iconProps={{iconName: "Cancel"}} text="Cancel" onClick={() => { props.onDismiss(false) }} />
            </Stack>}
        >
            <Pivot>
                <PivotItem key="siteDetails" headerText="Details">
                    { error !== undefined && (
                        <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
                    )}
                    <TextField disabled={asRoomApprover} autoFocus label="Room Name" value={name} onChange={(e, newValue) => setName(newValue)} errorMessage={!formValid ? "Room Name is mandatory" : undefined} />
                    <TextField type="number" min={1} max={900} label="Capacity" value={capacity ? capacity.toString() : undefined} onChange={(e, newValue) => {
                        if (newValue) {
                            const numVal = parseInt(newValue, 10);
                            setCapacity(numVal);
                        } else if (newValue === undefined || newValue.trim() === "") {
                            setCapacity(undefined);
                        }
                    }} />
                    <TextField multiline label="Equipment / Other Information" value={equipment} onChange={(e, newValue) => setEquipment(newValue)} />
                    <Toggle label="Enable / Disable Room" onText="Disabled" offText="Enabled" checked={disabled} onChange={(e, checked) => setDisabled(checked)} />
                    <Toggle label="Visibility" onText="Hidden from users, available to administrators" offText="Visible to all users" checked={hidden} onChange={(e, checked) => setHidden(checked)} />
                </PivotItem>
                <PivotItem key="restrictions" headerText="Restrictions">
                    <RestrictionsSection
                        resourceId={room.Id || -1}
                        onChange={(constraints, checkIn) => {
                            setRoomConstraint(constraints.roomBookingConstraint);
                            setEnableCheckIn(checkIn);
                        }}
                        updateType={RestrictionUpdate.Site}
                        roomBookingConstraint={roomConstraint}
                        roomCheckIn={enableCheckIn}
                    />
                </PivotItem>
                <PivotItem key="approval" headerText="Approval">
                    <Toggle label="Restrict room booking to Site Admins only" onText="Yes" offText="No" checked={restrictRoomAccess} onChange={(e, checked) => {
                        if (!checked && window.confirm('This will remove all room booking restrictions, allowing all users to book rooms. Restriction settings saved for individual users will be lost. Are you sure?')) setRestrictRoomAccess(checked);
                        if (checked) setRestrictRoomAccess(checked);
                    }} />
                    { restrictRoomAccess === true && (
                        <Stack horizontal verticalAlign="center" tokens={{childrenGap: 10}}>
                            <NormalPeoplePicker
                                selectedItems={roomAdmins}
                                onResolveSuggestions={roomUserSearch}
                                onChange={(items) => {
                                    if (userContext?.Profile.Id) {
                                        if (
                                            roomAdmins?.find((sa: any) => sa.data.userId === userContext.Profile.Id) && 
                                            !items?.find((sa: any) => sa.data.userId === userContext.Profile.Id)
                                        ) {
                                            alert('You cannot remove yourself from the room approval role. Please contact the system administrator if you should no longer be a room approver');
                                            return;
                                        }
                                    }
                                    setRoomAdmins(items);
                                }}
                            />
                            <Checkbox label="Include Teams" checked={roomIncludeTeams} onChange={(e, checked) => setRoomIncludeTeams(checked)}/>
                        </Stack>
                    )}
                </PivotItem>
                <PivotItem key="qrCode" headerText="QR Code">
                    { room.Id === undefined && (
                        <MessageBar messageBarType={MessageBarType.warning}>Save the room details to generate a QR code</MessageBar>
                    )}
                    { room.Id !== undefined && (
                        <Stack horizontal verticalAlign="start" tokens={{childrenGap: 20}}>
                            <div><img src={qrCode} alt="QR Code" /></div>
                            <div>
                                <h3>QR Code:</h3>
                                <p>This QR code can be used to check in to this room.</p>
                                <p>Download it for use in print:</p>
                                <PrimaryButton disabled={qrCode === undefined} iconProps={{iconName: "Download"}} text="Download" onClick={() => {
                                    if (qrCode) {
                                        const a = document.createElement('a');
                                        a.href = qrCode;
                                        const name = `${room.SiteName?.replace(/\s/,'-')}_${room.LocationName?.replace(/\s/,'-')}_${room.Name?.replace(/\s/,'-')}_qr_code.png`
                                        a.download = `${name || "room"}-qr-code.png`;
                                        a.click();
                                    }
                                }} />
                            </div>
                        </Stack>
                    )}
                </PivotItem>
            </Pivot>
        </Panel>
    )
}